import { formatCompactMoney } from "./deal-card-facts";

export type OsDigestCopyInput = {
  people: Array<{
    name: string;
    listingCount: number;
    side: "buyer" | "seller" | "other";
  }>;
  listings: Array<{
    title: string;
    location: string | null;
    askingPriceAmount: number | null;
  }>;
};

function joinNames(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** Inbox subject: who is waiting, and on how many listings when that is the story. */
export function buildOsDigestSubject(input: OsDigestCopyInput): string {
  const { people } = input;
  if (people.length === 0) return "Your pipeline is waiting on you";
  const allBuyers = people.every((person) => person.side === "buyer");
  const person = people[0];
  if (people.length === 1 && person) {
    if (allBuyers && person.listingCount > 1) {
      return `${person.name} is waiting on a reply about ${person.listingCount} listings`;
    }
    if (allBuyers && person.listingCount === 1) {
      return `${person.name} is waiting on a reply`;
    }
    return `${person.name} is waiting on you`;
  }
  if (allBuyers) return `${people.length} buyers are waiting on your reply`;
  return `${people.length} leads are waiting on you`;
}

export function buildOsDigestHeadline(input: OsDigestCopyInput): string {
  const count = input.people.length;
  const allBuyers =
    count > 0 && input.people.every((person) => person.side === "buyer");
  if (count === 1) {
    return allBuyers ? "1 buyer is waiting on you" : "1 lead is waiting on you";
  }
  return allBuyers
    ? `${count} buyers are waiting on you`
    : `${count} leads are waiting on you`;
}

/** Preheader: the businesses, where they are, and the asking price in view. */
export function buildOsDigestPreheader(input: OsDigestCopyInput): string {
  const titles = [
    ...new Set(input.listings.map((listing) => listing.title).filter(Boolean)),
  ].slice(0, 3);
  const locations = [
    ...new Set(
      input.listings
        .map((listing) => listing.location)
        .filter((location): location is string => Boolean(location)),
    ),
  ];
  const total = input.listings.reduce(
    (sum, listing) => sum + (listing.askingPriceAmount ?? 0),
    0,
  );
  const totalLabel = total > 0 ? formatCompactMoney(total) : null;
  if (titles.length > 0) {
    const place = locations.length === 1 ? ` in ${locations[0]}` : "";
    const businesses = `${joinNames(titles)}${place}`;
    return totalLabel
      ? `${businesses}. ${totalLabel} in asking price.`
      : `${businesses}.`;
  }
  const names = input.people
    .map((person) => person.name)
    .filter(Boolean)
    .slice(0, 2);
  if (names.length === 0) return "A lead in your pipeline is waiting on you.";
  return `${joinNames(names)} ${names.length === 1 ? "is" : "are"} waiting on you.`;
}
